import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getProxiedImageUrl } from '@/utils/imageProxy';

export const useAPISportsData = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch claim requests
  const { data: claimRequests, isLoading: claimsLoading, refetch: refetchClaims } = useQuery({
    queryKey: ['api-athlete-claim-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('api_athlete_claim_requests')
        .select(`
          *,
          api_athletes (
            id,
            name,
            sport,
            photo_url,
            current_team_name
          ),
          profiles!api_athlete_claim_requests_profile_id_fkey (
            id,
            display_name,
            email,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Approve/reject claim mutation
  const approveClaim = useMutation({
    mutationFn: async ({ claimRequestId, approved, adminNotes }: { 
      claimRequestId: string; 
      approved: boolean; 
      adminNotes?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('approve-athlete-claim', {
        body: { claimRequestId, approved, adminNotes },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast({
        title: variables.approved ? 'Claim Approved' : 'Claim Rejected',
        description: `Successfully ${variables.approved ? 'approved' : 'rejected'} the claim request`,
      });
      queryClient.invalidateQueries({ queryKey: ['api-athlete-claim-requests'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to process claim',
        variant: 'destructive',
      });
    },
  });

  // Fetch creator claim requests
  const { data: creatorClaimRequests, isLoading: creatorClaimsLoading, refetch: refetchCreatorClaims } = useQuery({
    queryKey: ['creator-claim-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('creators')
        .select('*')
        .not('claimed_by_profile_id', 'is', null)
        .in('claim_status', ['pending', 'approved', 'rejected'])
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const profileIds = [...new Set((data || []).map(c => c.claimed_by_profile_id).filter(Boolean))];
      let profilesMap: Record<string, any> = {};
      if (profileIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, email, avatar_url')
          .in('id', profileIds);
        profiles?.forEach(p => { profilesMap[p.id] = p; });
      }

      return (data || []).map(c => ({
        ...c,
        claimer_profile: profilesMap[c.claimed_by_profile_id!] || null,
      }));
    },
  });

  // Approve/reject creator claim mutation
  const approveCreatorClaim = useMutation({
    mutationFn: async ({ creatorId, approved, adminNotes }: {
      creatorId: string;
      approved: boolean;
      adminNotes?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('approve-athlete-claim', {
        body: { claimRequestId: creatorId, approved, claimType: 'creator', adminNotes },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast({
        title: variables.approved ? 'Creator Claim Approved' : 'Creator Claim Rejected',
        description: `Successfully ${variables.approved ? 'approved' : 'rejected'} the creator claim`,
      });
      queryClient.invalidateQueries({ queryKey: ['creator-claim-requests'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to process creator claim',
        variant: 'destructive',
      });
    },
  });

  return {
    claimRequests,
    claimsLoading,
    refetchClaims,
    approveClaim: approveClaim.mutate,
    approvingClaim: approveClaim.isPending,
    creatorClaimRequests: creatorClaimRequests || [],
    creatorClaimsLoading,
    refetchCreatorClaims,
    approveCreatorClaim: approveCreatorClaim.mutate,
    approvingCreatorClaim: approveCreatorClaim.isPending,
  };
};
